import icons from '../../../components/generic/Icon/icons';
import { useDialogContext } from '../../../contexts/DialogContext';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { remove } from '../../../redux/api';
import type { CustomSeverityScope } from '../../../../shared/model-typings/customseverity';

type DeleteCustomRiskScoreVariables = {
  issueId: number;
  scope: {
    type: CustomSeverityScope;
    id: string;
  };
};

const deleteCustomRiskScore = ({ issueId, scope }: DeleteCustomRiskScoreVariables) =>
  remove(`/api/custom-risk-scores/${issueId}`, { query: { scope } });

/**
 * Hook for deleting a custom risk score from an issue with confirmation dialog.
 */
export default function useDeleteCustomRiskScore() {
  const { onConfirm } = useDialogContext();

  const mutation = useToastedMutation({
    mutationFn: deleteCustomRiskScore,
    successToastMessage: 'Custom risk score removed successfully',
    errorToastMessage: 'Failed to remove custom risk score',
  });

  const onDeleteCustomRiskScore = (
    variables: DeleteCustomRiskScoreVariables,
    options?: { onSuccess?: () => void },
  ) => {
    onConfirm({
      title: 'Remove Custom Risk Score',
      body: 'If you proceed, the original CVSS score will become the only severity indicator in the issues table.',
      confirmText: 'Remove',
      confirmIcon: icons.trashRegular,
      onConfirm: () => {
        mutation.mutate(variables, { onSuccess: options?.onSuccess });
      },
    });
  };

  return {
    onDeleteCustomRiskScore,
    isDeleting: mutation.isPending,
  };
}
